// Tree Parallax Effect Module
// Uses CSS custom properties and Intersection Observer for optimal performance

class TreeParallax {
  constructor(element) {
    this.element = element;
    this.isInView = false;
    this.scrollY = 0;
    this.ticking = false;
    
    this.init();
  }

  init() {
    if (!this.element) return;

    this.setupIntersectionObserver();
    
    this.setupScrollListener();
    
    this.element.style.setProperty('--parallax-y', '0px');
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isInView = entry.isIntersecting;
        
        if (!this.isInView) {
          // Reset transform when out of view
          this.element.style.setProperty('--parallax-y', '0px');
        }
      });
    }, options);

    this.observer.observe(this.element);
  }

  setupScrollListener() {
    if (window.app && window.app.lenis) {
      window.app.lenis.on('scroll', ({ scroll, velocity }) => {
        this.scrollY = scroll;
        this.updateParallax();
      });
    } else {
      window.addEventListener('scroll', () => {
        this.scrollY = window.pageYOffset;
        this.requestTick();
      }, { passive: true });
    }
  }

  requestTick() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateParallax();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateParallax() {
    if (!this.isInView) return;

    // Calculate parallax offset
    const rect = this.element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Calculate how much of the element is visible
    const elementTop = rect.top;
    const elementHeight = rect.height;
    
    // Normalize the scroll position relative to the element
    const scrollProgress = (windowHeight - elementTop) / (windowHeight + elementHeight);
    
    // Apply parallax effect (negative value for upward movement)
    const parallaxOffset = Math.max(-100, Math.min(100, scrollProgress * -220));
    
    // Use CSS custom property for smooth hardware-accelerated animation
    this.element.style.setProperty('--parallax-y', `${parallaxOffset}px`);
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (window.app && window.app.lenis) {
      // Remove Lenis listener if needed
      // Note: Lenis doesn't have a direct removeListener method
      // so we'll rely on the instance being destroyed
    }
  }
}

export function initTreeParallaxCSS(treeContainer) {
  if (!treeContainer) return;

  treeContainer.style.transform = 'translateY(var(--parallax-y, 0px)) translateZ(0)';
  
  const parallaxController = new TreeParallax(treeContainer);
  
  return parallaxController;
}

export default TreeParallax; 